import {SyncStatus} from '../offline/entity.js';
import {SMOKEHOUSE_LOCAL_ENTITY_TYPES} from './offline.js';
import {SMOKER_ENTITY_TYPE} from './smoker-store.js';

const ACTION_BY_STATUS=Object.freeze({
  [SyncStatus.PENDING_CREATE]:'create',
  [SyncStatus.PENDING_UPDATE]:'update',
  [SyncStatus.PENDING_DELETE]:'delete'
});

function syncPriority(entity){
  if(entity?.entityType!==SMOKER_ENTITY_TYPE)return 10;
  if(entity.syncStatus===SyncStatus.CONFLICT)return 0;
  if(entity.isDefault===false)return 1;
  if(entity.isDefault===true)return 20;
  return 10;
}

function orderPending(rows=[]){
  return [...rows].sort((left,right)=>
    syncPriority(left)-syncPriority(right)
    ||String(left?.updatedAt||left?.createdAt||'').localeCompare(String(right?.updatedAt||right?.createdAt||''))
    ||String(left?.id||'').localeCompare(String(right?.id||''))
  );
}

function requireDependencies(repository,queue,remoteAdapters){
  if(!repository?.put||!repository?.remove)throw new TypeError('repository with put/remove is required');
  if(!queue?.listReady||!queue?.markAttempt||!queue?.markFailure||!queue?.markConflict){
    throw new TypeError('retry-aware sync queue contract is required');
  }
  if(!remoteAdapters||typeof remoteAdapters!=='object')throw new TypeError('remoteAdapters are required');
}

function syncedEntity(entity,result={}){
  const remote=result.entity&&typeof result.entity==='object'?result.entity:{};
  return {
    ...entity,
    ...remote,
    id:entity.id,
    syncStatus:SyncStatus.SYNCED,
    serverVersion:Number(result.serverVersion??remote.serverVersion??entity.serverVersion??0)||0,
    syncAttempts:0,
    lastSyncAttemptAt:entity.lastSyncAttemptAt||null,
    lastSyncFailureAt:null,
    lastSyncErrorCode:null,
    nextSyncAttemptAt:null,
    conflict:null
  };
}

export function createSmokehouseSyncProcessor({
  repository,
  queue,
  remoteAdapters,
  maxBatch=100
}={}){
  requireDependencies(repository,queue,remoteAdapters);
  const batchLimit=Number(maxBatch);
  if(!Number.isInteger(batchLimit)||batchLimit<1||batchLimit>1000)throw new TypeError('maxBatch must be between 1 and 1000');

  async function processOne(entity){
    const type=entity?.entityType;
    if(!SMOKEHOUSE_LOCAL_ENTITY_TYPES.includes(type)){
      return Object.freeze({status:'ignored',entityType:type||null,id:entity?.id||null});
    }
    if(entity.syncStatus===SyncStatus.CONFLICT){
      return Object.freeze({status:'conflict_pending',entityType:type,id:entity.id});
    }

    const action=ACTION_BY_STATUS[entity.syncStatus];
    if(!action)return Object.freeze({status:'ignored',entityType:type,id:entity.id});

    const adapter=remoteAdapters[type];
    if(typeof adapter?.[action]!=='function'){
      return Object.freeze({status:'adapter_missing',entityType:type,id:entity.id,action});
    }

    const attempted=await queue.markAttempt(type,entity);
    try{
      const result=await adapter[action](attempted);

      if(result?.status==='conflict'){
        const conflicted=await queue.markConflict(type,attempted,{
          reason:String(result.reason||'version_mismatch'),
          remoteServerVersion:Number(result.remoteServerVersion)||0
        });
        return Object.freeze({status:'conflict',entityType:type,id:entity.id,entity:conflicted});
      }

      if(!result||result.ok!==true){
        const failed=await queue.markFailure(type,attempted,String(result?.errorCode||'REMOTE_RETRY_REQUESTED'));
        return Object.freeze({status:'retry',entityType:type,id:entity.id,action,entity:failed});
      }

      if(action==='delete'){
        await repository.remove(type,entity.id);
        return Object.freeze({status:'synced_delete',entityType:type,id:entity.id});
      }

      const synced=syncedEntity(attempted,result);
      await repository.put(type,synced);
      return Object.freeze({status:'synced',entityType:type,id:entity.id,entity:synced});
    }catch(error){
      const errorCode=String(error?.code||error?.name||'SYNC_FAILED').slice(0,80);
      const failed=await queue.markFailure(type,attempted,errorCode);
      return Object.freeze({
        status:'retry',
        entityType:type,
        id:entity.id,
        action,
        errorCode,
        entity:failed
      });
    }
  }

  async function processPending(){
    const pending=await queue.listReady();
    const selected=orderPending(
      (pending||[]).filter(entity=>SMOKEHOUSE_LOCAL_ENTITY_TYPES.includes(entity.entityType))
    ).slice(0,batchLimit);

    const results=[];
    for(const entity of selected)results.push(await processOne(entity));

    return Object.freeze({
      processed:results.length,
      synced:results.filter(item=>item.status==='synced'||item.status==='synced_delete').length,
      conflicts:results.filter(item=>item.status==='conflict'||item.status==='conflict_pending').length,
      retries:results.filter(item=>item.status==='retry'||item.status==='adapter_missing').length,
      results:Object.freeze(results)
    });
  }

  return Object.freeze({processOne,processPending});
}
